"use strict";

import DragDelegate from "/js/util/drag-delegate.js";
import AnimSequencer from "/js/util/anim-sequencer.js";
import Autoshrink from "/js/util/autoshrink.js";
import Global from "/js/core/global.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util.js";
import CardsHolderView from '/js/top-level-views/cards-holder-view.js';

/**
 * Lesson card base class.
 */
export default class CardView {

  #$el = null;

  #dragDelegate = null;

  #data = null;
  #dragStartX = NaN;
  #dragStartY = NaN;
  #dragStartTranslationX = NaN;
  #dragStartTranslationY = NaN;
  #isDragDismissMode = false;
  #isDragRecallMode = false;
  #defaultTranslationX = NaN;
  #defaultTranslationY = NaN;
  #shouldIgnoreDrag = false;
  #isActive = false;

  /**
   * @param data is the card instance's data (which originates from the lesson json file).
   * @param explicitSkillId is optional
   */
  constructor(data, explicitSkillId=null) {
    this.#data = data;

    // Make the DOM element. Note how it has a class which is the name of the layout.
    const cls = 'card' + ' ' + this.#data.layout;
    this.#$el = $('<div/>', { 'class': cls });

    // Make the content that goes inside.
    const content = this.makeContent(explicitSkillId);
    this.#$el.append(content);

    this.#dragDelegate = new DragDelegate(this, this.#$el);
    this.#dragDelegate.setCallbacks(
        this.onDelegateDown, this.onDelegateMove, this.onDelegateUp, this.onDelegateClick);
  }

  get $el() {
    return this.#$el;
  }

  get data() {
    return this.#data;
  }

  get dragDelegate() {
    return this.#dragDelegate;
  }

  /**
   * Responsible for generating the html string of the card's content. 
   *
   * <p>Subclasses must override this.
   */
  makeContent(explicitSkillId=null) { return null; }

  /**
   * Gets called when the stack with the given card is shown for the first time.
   * Subclass may need to do things here that require card being in the displaylist
   * (eg, autoshrink action).
   */
  onFirstShow() {
    // Most card subtypes will have a ".text" div that should be auto-shrunk...
    Autoshrink.go(this.$el.find(".text"), this.$el.find(".text .inner"))
  }

  /**
   * Used to ensure elements are enabled and/or reachable
   * only when card is the top-most card in its stack.
   *
   * When overriding, subclass should call `super.isOnTop`
   */
  set isActive(b) {
    this.#isActive = b;
    Util.setAriaHidden(this.#$el, !b);
  }

  get isActive() {
    return this.#isActive;
  }
  /*
   * Should be called to interrupt the drag delegate's stuff which was in progress.
   */
  cancelDragAction() {
    this.#dragDelegate.clearDragHandlers();
    this.#$el.removeClass('noTransition');
    // Move back to default position for good measure.
    Util.setTranslation(this.#$el, this.#defaultTranslationX, this.#defaultTranslationY);
    this.#shouldIgnoreDrag = true;
  }

  onDelegateClick(x, y, deltaX, deltaY, e) {
    this.#$el.removeClass('noTransition');
    this.#$el.trigger('card-click');
  }

  onDelegateDown(x, y) {
    if (!this.isActive) {
      this.#shouldIgnoreDrag = true;
      return;
    }
    this.#shouldIgnoreDrag = false;

    // Disable css transitions for duration of mouse/touch gesture.
    this.#$el.removeClass('noTransition');
    this.#$el.addClass('noTransition');
    
    this.#isDragDismissMode = false;
    this.#isDragRecallMode = false;
    const o = Util.getCssTranslation(this.#$el);
    this.#dragStartTranslationX = o.x;
    this.#dragStartTranslationY = o.y;

    if (isNaN(this.#defaultTranslationY)) {
      // Lazy init
      this.#defaultTranslationX = o.x;
      this.#defaultTranslationY = o.y;
    }
  }

  onDelegateMove(deltaX, deltaY, didCrossThreshold) {
    if (this.#shouldIgnoreDrag) {
      return;
    }

    if (didCrossThreshold) {
      const angle = Util.getAngle(deltaX, deltaY);
      const isDownish = angle > 180 - 45 && angle < 180 + 45;
      if (isDownish) {
        // cl('move - crossed threshold; recall mode');
        this.#isDragRecallMode = true;
      } else {
        // cl('move - crossed threshold; dismiss mode');
        this.#isDragDismissMode = true;
      }
    }

    if (!this.#isDragDismissMode) {
      return;
    }

    // correct for scale
    deltaX /= Global.cardScale;
    deltaY /= Global.cardScale;
    
    // clamp deltaX
    const range = this.#getDragXRange();
    deltaX = Util.clamp(deltaX, -range, range);
    
    const x = this.#dragStartTranslationX + deltaX;
    const y = this.#dragStartTranslationY + deltaY;

    // rotation
    const rot = this.#getRotationForDeltaX(deltaX);

    this.#setTransform(x, y, rot);
  }

  onDelegateUp(x, y, deltaX, deltaY) {
    if (this.#shouldIgnoreDrag) {
      return;
    }
    // Re-enable css transitions.
    this.#$el.removeClass('noTransition');

    const distance = Util.pointLength(deltaX, deltaY);
    if (distance < 12) {
      // Drag has finished near where it started. Snap back.
      // cl('up - will snap back')
      Util.setTranslation(this.#$el, this.#defaultTranslationX, this.#defaultTranslationY);
      return;
    }
    if (this.#isDragRecallMode) {
      if (deltaY < 0) {
        // cl('up - recall cancelled');
      } else {
        // cl('up - will recall');
        this.#$el.trigger("card-drag-previous");
      }
      return;
    }

    // Should advance.
    // cl('up - will dismiss.')
    this.#doDismissAnim(deltaX, deltaY);
  }  

  #doDismissAnim(deltaX, deltaY) {

    // correct for scale
    deltaX /= Global.cardScale;
    deltaY /= Global.cardScale;

    const range = this.#getDragXRange();
    deltaX = Util.clamp(deltaX, -range, range);
    
    const mag = this.#$el.height() * 1.5;
    const deltaPoint = { x: deltaX, y: deltaY };
    Util.normalizePoint(deltaPoint, mag);

    const x = this.#defaultTranslationX + deltaPoint.x;
    const y = this.#defaultTranslationY + deltaPoint.y;
    const rot = this.#getRotationForDeltaX(deltaX);

    const onAnimComplete = () => {
      this.#$el.trigger("card-drag-next");
    };
    AnimSequencer.runOne(
      this.#$el,
      () => { this.#setTransform(x, y, rot); },
      onAnimComplete() );
  }

  #getDragXRange() {
    const range = this.#$el.width() * (DragDelegate.isTouchDevice ? 0.75 : 0);
    return range;
  }

  #getRotationForDeltaX(deltaX) {
    const range = this.#getDragXRange();
    if (range == 0) {
      return 0;
    }
    const degrees = deltaX / range * 10;
    return degrees;
  }

  #setTransform(x, y, rotation) {
    const s = `translateX(${x}px) translateY(${y}px) rotate(${rotation}deg)`;
    Util.setTransform(this.#$el, s);
  }

  /** 
   * Given a "text" property from the card's json, creates an HTML string consisting of <p> tags, 
   * wrapped by an ".inner" div.
   */
  static makeHtmlFromJsonTextArray(data) {
    
    let s = "";
    if (!data.text) {
      return s;
    }

    for (let i = 0; i < data.text.length; i++) {
      
      s += "<p>" + data.text[i].content + "</p>";
      
      // Tests:
      // extra
      // s += "<p>" + data.text[i].content + "Searching all of Japan for handcrafted items that express its heart and soul, our proprietor, KASHIYUKA, presents things that bring a bit of luxury." + "</p>";
      // extra long
      // s += "<p>" + data.text[i].content + "Searching all of Japan for handcrafted items that express its heart and soul, our proprietor, KASHIYUKA, presents things that bring a bit of luxury to everyday life. The theme this time is in keeping with Casa BRUTUS’ Special Edition on Tokyo and features crafts from the period when the town was known as Edo, from 1603 to 1868. In Tokyo’s Nihonbashi district, in a shop that’s stood continuously for three centuries, she came upon a handcrafted Western-style clothing brush." + "</p>";
    }

    s = `<div class="inner">${ s }</div>`;
    return s;
  }
}
